import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Option } from '@/components/inputs/AutocompleteInput';
import { SelectInputOption } from '@/components/inputs/SelectInput';
import {
  useGetBranchesByCommerceQuery,
  useGetPickupCommercesQuery,
} from '@/pages/Business/pages/PickupCycles/services';
import {
  BulkCreatePickupCyclePayload,
  DEFAULT_BRANCH_VALUE,
  defaultPickupCycleFormValues,
  isDefaultBranchValue,
  PickupBranch,
  PickupCycle,
  PickupCycleFormValues,
  pickupCycleSchema,
  WEEK_DAY_KEYS,
} from '@/pages/Business/pages/PickupCycles/models';
import {
  cycleTimeToDate,
  dateToCycleTime,
  findCentralBranch,
  formatBranchAddress,
} from '@/pages/Business/pages/PickupCycles/utils';

interface UsePickupCycleFormProps {
  initialData: PickupCycle | null;
  open: boolean;
  onSubmit: (payload: BulkCreatePickupCyclePayload) => void;
}

export const usePickupCycleForm = ({
  initialData,
  open,
  onSubmit,
}: UsePickupCycleFormProps) => {
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PickupCycleFormValues>({
    resolver: zodResolver(pickupCycleSchema),
    defaultValues: defaultPickupCycleFormValues,
  });

  const commerceId = watch('commerceId');
  const branchId = watch('branchId');
  const branchIds = watch('branchIds');
  const loadedCommerceRef = useRef<string>('');

  const { data: commerces = [], isLoading: isCommercesLoading } =
    useGetPickupCommercesQuery();

  const { data: branches = [], isFetching: isBranchesLoading } =
    useGetBranchesByCommerceQuery(
      { commerceId },
      { skip: !commerceId },
    );

  const isEdit = Boolean(initialData);

  useEffect(() => {
    if (!open) return;

    if (initialData) {
      reset({
        commerceId: initialData.commerceId,
        branchId: initialData.branchId ?? DEFAULT_BRANCH_VALUE,
        branchIds: [initialData.branchId ?? DEFAULT_BRANCH_VALUE],
        cycleTime: cycleTimeToDate(initialData.cycleTime),
        tag: initialData.tag ?? '',
        monday: initialData.monday,
        tuesday: initialData.tuesday,
        wednesday: initialData.wednesday,
        thursday: initialData.thursday,
        friday: initialData.friday,
        saturday: initialData.saturday,
        sunday: initialData.sunday,
      });
      loadedCommerceRef.current = initialData.commerceId;
      return;
    }

    reset(defaultPickupCycleFormValues);
    loadedCommerceRef.current = '';
  }, [initialData, open, reset]);

  // Al cambiar el comercio, limpiar las sucursales elegidas (vuelve a "Casa central").
  useEffect(() => {
    if (!open) return;
    if (commerceId && commerceId !== loadedCommerceRef.current) {
      setValue('branchId', DEFAULT_BRANCH_VALUE);
      setValue('branchIds', [DEFAULT_BRANCH_VALUE]);
    }
  }, [commerceId, open, setValue]);

  const commerceOptions: SelectInputOption[] = useMemo(
    () => commerces.map((commerce) => ({
      label: commerce.name,
      value: commerce.id,
    })),
    [commerces],
  );

  // El alta usa un multiselect (Option) y la edición el select de a uno
  // (SelectInputOption), así que las sucursales se exponen en los dos formatos.
  const branchSingleOptions: SelectInputOption[] = useMemo(
    () => [
      { label: 'Casa central (por defecto)', value: DEFAULT_BRANCH_VALUE },
      ...branches.map((branch) => ({ label: branch.name, value: branch.id })),
    ],
    [branches],
  );

  const branchOptions: Option[] = useMemo(
    () => [
      { label: 'Casa central (por defecto)', data: DEFAULT_BRANCH_VALUE },
      ...branches.map((branch) => ({ label: branch.name, data: branch.id })),
    ],
    [branches],
  );

  const activeBranchIds = useMemo(
    () => (isEdit ? [branchId] : branchIds),
    [isEdit, branchId, branchIds],
  );

  const selectedBranches: PickupBranch[] = useMemo(() => {
    const central = findCentralBranch(branches);
    return activeBranchIds
      .map((id) =>
        isDefaultBranchValue(id)
          ? central
          : branches.find((branch) => branch.id === id),
      )
      .filter((branch): branch is PickupBranch => Boolean(branch));
  }, [activeBranchIds, branches]);

  // Las sucursales sin dirección no generan retiro: se avisan en el modal para
  // que no queden agendadas en silencio.
  const branchesWithoutAddress = useMemo(
    () => selectedBranches.filter((branch) => !formatBranchAddress(branch.address)),
    [selectedBranches],
  );

  const isUsingDefaultBranch = activeBranchIds.some(isDefaultBranchValue);

  const selectAllBranches = () => {
    setValue(
      'branchIds',
      branches.map((branch) => branch.id),
      { shouldValidate: true },
    );
  };

  const clearBranches = () => {
    setValue('branchIds', [DEFAULT_BRANCH_VALUE], { shouldValidate: true });
  };

  const onFormSubmit = handleSubmit((data) => {
    // El centinela de casa central sólo tiene sentido solo: si además se
    // eligieron sucursales concretas, mandan ellas. Vacío = casa central, que
    // es como el back guarda el branch_id nulo.
    const chosen = isEdit ? [data.branchId] : data.branchIds;
    const explicitBranchIds = chosen.filter((id) => !isDefaultBranchValue(id));

    onSubmit({
      commerceId: data.commerceId,
      branchIds: explicitBranchIds,
      cycleTime: dateToCycleTime(data.cycleTime),
      tag: data.tag.trim() !== '' ? data.tag.trim() : null,
      monday: data.monday,
      tuesday: data.tuesday,
      wednesday: data.wednesday,
      thursday: data.thursday,
      friday: data.friday,
      saturday: data.saturday,
      sunday: data.sunday,
    });
  });

  const daysError = (errors as Record<string, { message?: string }>).days
    ?.message;

  return {
    control,
    onFormSubmit,
    commerceOptions,
    branchOptions,
    branchSingleOptions,
    isCommercesLoading,
    isBranchesLoading: Boolean(commerceId) && isBranchesLoading,
    selectedBranches,
    branchesWithoutAddress,
    selectedBranchCount: selectedBranches.length,
    isUsingDefaultBranch,
    hasCommerceSelected: Boolean(commerceId),
    hasBranches: branches.length > 0,
    selectAllBranches,
    clearBranches,
    daysError,
    dayKeys: WEEK_DAY_KEYS,
    isEdit,
  };
};
